/**
 * Deliverability rollups (admin doc 26 §3.5 item 2): per channel, how many messages a real
 * gateway took over the window and what the providers said back.
 *
 * Email carries the lines alert row 72 pages on (SES bounces and complaints), drawn beside the
 * rate so a number near the line reads as near the line. The alarm needs enough sends to be a
 * pattern, the same floor the page uses — the server decides it, so the two never disagree.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Select } from '../components/forms';
import { ErrorState, LoadingState } from '../components/feedback';
import { Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { useDeliverability, type DeliverabilityRow } from '../lib/api/hooks/notifications';

const WINDOWS = ['1', '7', '30'];

/** `part` of `whole` as a percentage with two places; a channel that sent nothing reads "—". */
export function rate(part: number, whole: number): string {
  return whole === 0 ? '—' : `${((100 * part) / whole).toFixed(2)}%`;
}

/** The rate with its line beside it, where a provider holds us to one. */
export function withLine(part: number, whole: number, line: number | null | undefined): string {
  const shown = rate(part, whole);
  return line == null ? shown : `${shown} (line ${line}%)`;
}

function state(row: DeliverabilityRow) {
  if (row.alarming) return <StatusChip tone="error" label="past the line" />;
  if (row.sent === 0) return <StatusChip tone="neutral" label="nothing sent" />;
  return <StatusChip tone="success" label="within" />;
}

export function DeliverabilityPanel() {
  const [days, setDays] = useState('7');
  const rollup = useDeliverability(Number(days));
  const items = rollup.data?.items ?? [];

  return (
    <Section
      title="Deliverability"
      note="Messages a real gateway took, and what the providers reported back"
      pad={false}
    >
      <div style={{ padding: 'var(--sp-md)' }}>
        <Select label="Window, days" value={days} onChange={setDays} options={WINDOWS} />
      </div>
      {rollup.isPending && <LoadingState message="Reading deliverability…" />}
      {rollup.isError && (
        <ErrorState title="Could not read deliverability" cause={errorMessage(rollup.error)} />
      )}
      {items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'channel', label: 'Channel' },
            { key: 'sent', label: 'Sent' },
            { key: 'delivered', label: 'Delivered' },
            { key: 'bounced', label: 'Bounced' },
            { key: 'spam', label: 'Spam reports' },
            { key: 'failed', label: 'Failed' },
            { key: 'state', label: '' },
          ]}
          rows={items.map((row) => ({
            id: row.channel,
            channel: row.channel,
            sent: row.sent,
            delivered: rate(row.delivered, row.sent),
            bounced: withLine(row.bounced, row.sent, row.bounce_max_percent),
            spam: withLine(row.spam_reports, row.sent, row.complaint_max_percent),
            failed: rate(row.failed, row.sent),
            state: state(row),
          }))}
        />
      )}
    </Section>
  );
}
